import { MouseEvent, memo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import {
  Avatar,
  Divider,
  IconButton,
  ListItemAvatar,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useTranslation } from "react-i18next";
import { useToCDNUrl } from "hooks/useCDN";
import { appStateMutate } from "stores/modules/app.state/operations";
import { updateRoomEffect } from "stores/modules/entities.room.effects/operations";
import { ReactComponent as SoundMasterIcon } from "containers/SceneListItem/SoundMaster.svg";

type EffectListItemProps = {
  roomId: string;
  effectId: string;
};

const EffectListItem = ({ roomId, effectId }: EffectListItemProps) => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation();
  const toCDNUrl = useToCDNUrl();

  const effect = useAppSelector((state) =>
    store.getRoomEffectById(state, effectId)
  );

  const onEdit = useCallback(() => {
    dispatch(
      appStateMutate((state) => {
        state.openRoomEffect = true;
        state.openRoomEffectId = effectId;
      })
    );
  }, [effectId, dispatch]);
  const onPlay = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();

      dispatch(
        updateRoomEffect(roomId, effectId, {
          playTime: Date.now(),
        })
      );
    },
    [roomId, effectId, dispatch]
  );

  return (
    <>
      <ListItemButton onClick={onEdit} style={{ paddingRight: 64 }}>
        <ListItemAvatar>
          <Avatar src={toCDNUrl(effect.imageUrl)} />
        </ListItemAvatar>
        <ListItemText
          primary={effect.name || t("新しいエフェクト")}
          secondaryTypographyProps={{ noWrap: true }}
          secondary={
            effect.soundName ? (
              <>
                {effect.soundRef && (
                  <Tooltip
                    title={t(
                      "この音源の再生にはサウンドマスタープランへの加入が必要です。"
                    )}
                  >
                    <SoundMasterIcon
                      width={12}
                      height={12}
                      style={{
                        marginRight: 4,
                        verticalAlign: "middle",
                        opacity: 0.7,
                        filter: "grayscale(100%)",
                        position: "relative",
                        top: "-1px",
                      }}
                    />
                  </Tooltip>
                )}
                {effect.soundName}
              </>
            ) : (
              "-"
            )
          }
        />
        <ListItemSecondaryAction>
          <IconButton
            onClick={onPlay}
            size="large"
            aria-label={t("カットインを再生")}
          >
            <PlayArrowIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItemButton>
      <Divider variant="inset" component="li" />
    </>
  );
};

export default memo(EffectListItem);
